<script setup>
import axios from 'axios';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';
import { useAuthStore } from '../../../../stores/auth';
import AccessBlockedNotice from '../AccessBlockedNotice.vue';
import AuthTermsNotice from '../AuthTermsNotice.vue';
import RequestAccessModal from '../RequestAccessModal.vue';

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	currentFormStepData: {
		type: Object,
		default: () => ({}),
	},
});

const router = useRouter();
const authStore = useAuthStore();
const user = props.currentFormStepData.user || {};
const email = user.email || props.currentFormStepData.email || '';
// Existing account that is NOT yet a member of this hub → cross-hub registration.
// Show the "existing profile" notice; a same-hub member logs in with no message.
const isCrossHub = props.currentFormStepData.memberOfThisHub === false;

const passwordInput = ref('');
const isSubmitting = ref(false);
const errors = ref([]);
const turnstileToken = ref(false);
const accessBlockedMessage = ref('');
const requestAccessOpen = ref(false);

async function handleContinue() {
	if (!passwordInput.value || isSubmitting.value) return;

	isSubmitting.value = true;
	errors.value = [];

	try {
		const res = await axios.post('/api/sso/oauth/token', {
			username: email,
			password: passwordInput.value,
			grant_type: 'password',
			'cf-turnstile-response': turnstileToken.value,
		});

		if (res.data?.access_token) {
			// Identity confirmed. Authenticate follow-up calls and stash the token so
			// the custom-fields step can auto-log the member in after joining.
			axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
			authStore.setPendingTokenData(res.data);
			authStore.setPendingSsoUser(user);

			try {
				// Already a member of this hub → log in directly.
				await authStore.retrieveUserData();
				authStore.login({ ...res.data, role: authStore.userData?.role });
				authStore.setPendingTokenData(null);
				router.push('/');
			} catch (error) {
				const code = error.response?.data?.code;

				if (code === 'ACCESS_MANAGEMENT_BLOCKED') {
					// Authenticated, but access to this hub is revoked / not granted.
					// Do not proceed to join — surface the block and offer a request.
					accessBlockedMessage.value = error.response?.data?.message
						|| 'You do not currently have access to this Hub.';
					delete axios.defaults.headers.common['Authorization'];
					authStore.setPendingTokenData(null);
					authStore.setPendingSsoUser(null);
				} else if (code === 'REGISTRATION_REQUIRED' || error.response?.status === 403) {
					// Not a member yet → collect this hub's custom fields (auto-joins if none).
					props.changeFormStep('custom-fields', { user }, true);
				} else {
					errors.value = ['Something went wrong, please try again later.'];
					loadCaptcha();
				}
			}
		} else {
			errors.value = ['Incorrect password. Please try again.'];
			loadCaptcha();
		}
	} catch (error) {
		errors.value = [error.response?.data?.message || 'Incorrect password. Please try again.'];
		loadCaptcha();
	} finally {
		isSubmitting.value = false;
	}
}

function loadCaptcha() {
	const _env = window.location.hostname.split('.');
	if (!['qa', 'local'].includes(_env[1])) return;

	const script = document.createElement('script');
	script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
	script.async = true;
	script.onload = () => {
		if (document.querySelector('#turnstile-container')) {
			document.querySelector('#turnstile-container').innerHTML = '';
		}

		window.turnstile.render(document.querySelector('#turnstile-container'), {
			theme: 'light',
			appearance: 'interaction-only',
			sitekey: import.meta.env.VITE_TURNSTILE_SITEKEY,
			callback: function (token) {
				turnstileToken.value = token;
			},
		});
	};
	document.head.appendChild(script);
}

onMounted(() => {
	loadCaptcha();
});
</script>

<template>
	<div class="confirm-password">
		<div class="confirm-password__title">Welcome back</div>
		<div class="confirm-password__info" v-if="isCrossHub">
			We found an existing profile for this email. Confirm your password to continue.
		</div>
		<div class="bg-white">
			<pge-input-text type="email" name="email" label="Email address" :model="email" disabled required
				has-required-sign no-tabindex />
		</div>
		<div class="bg-white">
			<pge-input-text type="password" name="password" label="Password" :model="passwordInput"
				v-model="passwordInput" :submit-on-enter="handleContinue" required has-required-sign
				has-trailing-icon no-tabindex />
		</div>
		<div id="turnstile-container"></div>
		<AccessBlockedNotice v-if="accessBlockedMessage" @request="requestAccessOpen = true" />
		<div class="confirm-password__actions">
			<pge-button label="Use a different email" template="text"
				@click="() => props.changeFormStep('check-email')" />
			<pge-button v-if="!accessBlockedMessage" :label="isSubmitting ? 'Logging in...' : 'Log In'"
				@click="handleContinue" :disabled="!passwordInput || isSubmitting" />
			<pge-button v-else label="Request Access" @click="requestAccessOpen = true" />
		</div>
		<div class="confirm-password__errors" v-if="errors.length">
			<div v-for="error in errors" :key="error">{{ error }}</div>
		</div>
		<AuthTermsNotice :links="eventData?.settings?.links || []" />
		<div class="confirm-password__forgot" v-if="!accessBlockedMessage">
			<a class="link-purple" @click="() => props.changeFormStep('forgot-password', { email })">Forgot Password?</a>
		</div>

		<RequestAccessModal :visible="requestAccessOpen" :initial-email="email || ''" :event-data="eventData" @close="requestAccessOpen = false" />
	</div>
</template>

<style lang="scss" scoped>
.confirm-password {
	display: flex;
	flex-direction: column;
	gap: 18px;

	&__title {
		text-align: center;
		font-weight: 700;
		font-size: 18px;
		line-height: 1.5;
		color: #000;
	}

	&__info {
		text-align: center;
		font-size: 14px;
		line-height: 1.5;
		color: #49454f;
	}

	&__forgot {
		text-align: center;

		.link-purple {
			color: #583174;
			text-decoration: none;
			font-size: 14px;
			cursor: pointer;
		}
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}
}

.bg-white {
	background: white;
}
</style>
